import { useEffect, useRef, useState } from 'react'

/**
 * Whether to carry a finite CSS animation's class this render, re-running the
 * animation each time `replayOn` changes (BRAI-685).
 *
 * A CSS animation plays when its class arrives and not again while it stays.
 * A class left on for as long as the invitation is wanted therefore plays
 * exactly once, however much happens afterwards — which is why a Copilot
 * button that had been bobbing on arrival stayed perfectly still as new
 * suggestions came in behind it.
 *
 * Dropping the flag for a single frame takes the class off and puts it back,
 * which is what re-runs the animation. A frame rather than a tick: React would
 * otherwise batch the two updates into one commit and the DOM would never see
 * the class leave.
 *
 * Starts applied, so the opening play needs no gap before it and the class is
 * on the very first paint.
 *
 * @param active Whether the animation is wanted at all. Going false removes
 *   the class; going true again lets CSS play it on arrival, with no replay
 *   needed.
 * @param replayOn Any value whose change should re-run the animation — an
 *   unread count, a revision, a timestamp.
 * @returns Whether to apply the animation class.
 */
export function useAnimationReplay(active: boolean, replayOn: unknown): boolean {
  const [applied, setApplied] = useState(active)
  const previous = useRef(replayOn)

  useEffect(() => {
    if (!active) {
      setApplied(false)
      return undefined
    }
    if (previous.current === replayOn) {
      setApplied(true)
      return undefined
    }
    previous.current = replayOn
    setApplied(false)
    const frame = window.requestAnimationFrame(() => setApplied(true))
    return () => window.cancelAnimationFrame(frame)
  }, [active, replayOn])

  return applied
}
